// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * PDF dates.
 *
 * §7.9.4 defines exactly one format — D:YYYYMMDDHHmmSSOHH'mm' — and real
 * writers emit at least six variants of it: without the D: prefix, without
 * seconds, with a Z instead of an offset, with the closing apostrophe missing,
 * with the apostrophes replaced by colons, and truncated after the year.
 *
 * A malformed date is NOT a finding and must never be shown as one. It is an
 * artefact of the writer, and every one of those variants appears in
 * documents from banks, payroll systems and government publishers. What this
 * module does is read what it can, keep the original string, and say plainly
 * when it could not.
 */

export interface PdfDate {
  /** The string exactly as it appears in the file. */
  raw: string;
  /** Milliseconds since the epoch, or null when it could not be read. */
  epochMs: number | null;
  /** The declared offset from UTC in minutes, or null when none was given. */
  offsetMinutes: number | null;
  /** Human form, in the document's own declared offset. */
  display: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function parsePdfDate(raw: string | null): PdfDate | null {
  if (!raw) return null;
  const text = raw.trim();
  if (text.length === 0) return null;

  const body = text.startsWith('D:') ? text.slice(2) : text;
  const m = body.match(
    /^(\d{4})(\d{2})?(\d{2})?(\d{2})?(\d{2})?(\d{2})?\s*(?:(Z)|([+-])\s*(\d{2})['’:]?(\d{2})?)?/,
  );
  if (!m || !m[1]) return { raw: text, epochMs: null, offsetMinutes: null, display: text };

  const year = Number(m[1]);
  const month = m[2] ? Number(m[2]) : 1;
  const day = m[3] ? Number(m[3]) : 1;
  const hour = m[4] ? Number(m[4]) : 0;
  const minute = m[5] ? Number(m[5]) : 0;
  const second = m[6] ? Number(m[6]) : 0;

  if (
    year < 1000 || year > 9999 ||
    month < 1 || month > 12 || day < 1 || day > 31 ||
    hour > 23 || minute > 59 || second > 60
  ) {
    return { raw: text, epochMs: null, offsetMinutes: null, display: text };
  }

  let offsetMinutes: number | null = null;
  if (m[7] === 'Z') offsetMinutes = 0;
  else if (m[8]) {
    const sign = m[8] === '-' ? -1 : 1;
    offsetMinutes = sign * (Number(m[9]) * 60 + (m[10] ? Number(m[10]) : 0));
  }

  // Date.UTC of the local fields, then the declared offset removed, gives the
  // true instant. Without an offset the fields are read as they stand — which
  // is what the specification says a missing offset means.
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const epochMs = offsetMinutes === null ? asUtc : asUtc - offsetMinutes * 60_000;

  const pad = (n: number) => String(n).padStart(2, '0');
  const zone =
    offsetMinutes === null
      ? ''
      : offsetMinutes === 0
        ? ' UTC'
        : ` UTC${offsetMinutes < 0 ? '−' : '+'}${pad(Math.floor(Math.abs(offsetMinutes) / 60))}:${pad(
            Math.abs(offsetMinutes) % 60,
          )}`;

  const display = m[4]
    ? `${day} ${MONTHS[month - 1]} ${year}, ${pad(hour)}:${pad(minute)}${zone}`
    : `${day} ${MONTHS[month - 1]} ${year}`;

  return { raw: text, epochMs, offsetMinutes, display };
}

/**
 * An ISO 8601 date as XMP writes them. Kept separate from the PDF form on
 * purpose: XMP is ordinary ISO 8601 and needs none of the tolerance above.
 */
export function parseXmpDate(raw: string | null): PdfDate | null {
  if (!raw) return null;
  const text = raw.trim();
  if (!text) return null;
  const ms = Date.parse(text);
  if (Number.isNaN(ms)) return { raw: text, epochMs: null, offsetMinutes: null, display: text };
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const zoneMatch = text.match(/(Z|[+-]\d{2}:?\d{2})$/);
  return {
    raw: text,
    epochMs: ms,
    offsetMinutes: null,
    display: `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${pad(
      d.getUTCHours(),
    )}:${pad(d.getUTCMinutes())} UTC${zoneMatch && zoneMatch[1] !== 'Z' ? ` (written as ${zoneMatch[1]})` : ''}`,
  };
}
